import { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { useWorkspace } from "@/hooks/useWorkspace";
import { useIsMobile } from "@/hooks/use-mobile";
import { MyLibraryListSection } from "@/components/library/myLibrary/MyLibraryListSection";
import { useMyLibraryUrlSync } from "@/components/library/myLibrary/useMyLibraryUrlSync";
import { LibraryBulkBar } from "@/components/library/LibraryBulkBar";
import { LibraryItemDetail } from "@/components/library/LibraryItemDetail";
import { LibraryCategoryManager } from "@/components/library/LibraryCategoryManager";
import {
  buildMyLibraryReturnTo,
  emptyLibraryDraft,
  fetchLibraryItemDecryptedText,
  getLibrarySourceTypeLabel,
  libraryCopy,
  libraryItemStatusOptions,
  librarySourceTypeOptions,
  resolveReturnToHref,
  type LibraryItemStatus,
  type LibrarySourceType,
} from "@/lib/library";
import { AppSeo } from "@/components/AppSeo";

import {
  deriveFilteredItemNoun,
  matchesLibrarySearch,
  sortItems,
  toDraft,
  type EditableDraft,
  type LibraryRecord,
  type LibrarySortOption,
} from "@/components/library/myLibraryHelpers";
import { useImportReviewKeyboard } from "@/components/library/useImportReviewKeyboard";
import { useMyLibraryBulkActions } from "@/components/library/useMyLibraryBulk";
import { useMyLibraryCategoryActions } from "@/components/library/useMyLibraryCategoryActions";
import { useMyLibrarySearch } from "@/components/library/useMyLibrarySearch";
import { useMyLibraryDataLoader } from "@/components/library/useMyLibraryDataLoader";
import { useMyLibraryPersist } from "@/components/library/useMyLibraryPersist";
import { MyLibrarySearchPanel } from "@/components/library/MyLibrarySearchPanel";
import { MyLibraryHeader } from "@/components/library/MyLibraryHeader";
import { MyLibraryFiltersBar } from "@/components/library/MyLibraryFiltersBar";
import { MyLibraryDialogs } from "@/components/library/MyLibraryDialogs";
import { MyLibraryEditorBody } from "@/components/library/MyLibraryEditorBody";

const MyLibrary = () => {
  const { active, workspaces, libraryMode } = useWorkspace();
  const isMobile = useIsMobile();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();

  // Editor + UI state
  const [editorOpen, setEditorOpen] = useState(false);
  const [managerOpen, setManagerOpen] = useState(false);
  const [editorDraft, setEditorDraft] = useState<EditableDraft>(emptyLibraryDraft());
  const [editingItem, setEditingItem] = useState<LibraryRecord | null>(null);
  const [editorCategoryIds, setEditorCategoryIds] = useState<string[]>([]);
  const [selectedCategoryIds, setSelectedCategoryIds] = useState<string[]>([]);
  const [selectedStatusFilters, setSelectedStatusFilters] = useState<LibraryItemStatus[]>([]);
  const [selectedSourceTypes, setSelectedSourceTypes] = useState<LibrarySourceType[]>([]);
  const [librarySearchQuery, setLibrarySearchQuery] = useState("");
  const [sortBy, setSortBy] = useState<LibrarySortOption>("recent");
  const [defaultCategoryId, setDefaultCategoryId] = useState<string | null>(active?.default_library_category_id ?? null);
  const [selectMode, setSelectMode] = useState(false);
  const [selectedItemIds, setSelectedItemIds] = useState<Set<string>>(new Set());
  const [bulkBusy, setBulkBusy] = useState(false);
  const [selectedWorkspaceIds, setSelectedWorkspaceIds] = useState<string[]>([]);
  const [editorWorkspaceIds, setEditorWorkspaceIds] = useState<string[]>([]);
  const [moveTargetWorkspaceId, setMoveTargetWorkspaceId] = useState<string>("");

  const linkedItemId = searchParams.get("item");
  const returnTo = searchParams.get("returnTo") ?? "";

  const {
    importOpen,
    setImportOpen,
    importAnnouncement,
    setImportAnnouncement,
    handleImportOpenChange,
    handleImportEscapeKeyDown,
    handleImportReviewRowFocusChange,
    handleImportFocusRestored,
  } = useImportReviewKeyboard();

  const search = useMyLibrarySearch();

  // Resolve selectedItem against current items (need data loader first; use ref pattern)
  // We use a deferred lookup: items come from the loader hook which needs selectedItem
  // for the linked-excerpts effect. Pass a stub then re-derive after.
  const [selectedItemSlot, setSelectedItemSlot] = useState<LibraryRecord | null>(null);

  const data = useMyLibraryDataLoader({ active, workspaces, libraryMode, selectedItem: selectedItemSlot });
  const { items, setItems, categories, setCategories, categoryUsageCounts, setCategoryUsageCounts, loading, linkedExcerpts, refreshLibrary } = data;

  const validCategoryIds = useMemo(() => new Set(categories.map((category) => category.id)), [categories]);
  const myLibraryReturnTo = useMemo(
    () => buildMyLibraryReturnTo({
      itemId: linkedItemId,
      search: librarySearchQuery,
      categoryIds: selectedCategoryIds,
      statusFilters: selectedStatusFilters,
      sortBy,
    }),
    [librarySearchQuery, linkedItemId, selectedCategoryIds, selectedStatusFilters, sortBy],
  );

  const defaultCategory = useMemo(
    () => categories.find((category) => category.id === defaultCategoryId) ?? null,
    [categories, defaultCategoryId],
  );
  const selectedItem = useMemo(
    () => items.find((item) => item.id === linkedItemId) ?? null,
    [items, linkedItemId],
  );
  useEffect(() => { setSelectedItemSlot(selectedItem); }, [selectedItem]);

  const isNeutralDefault = defaultCategory?.name === "Uncategorized" && defaultCategory?.color === "silver";

  const getInitialEditorCategoryIds = useCallback(
    () => (defaultCategoryId && validCategoryIds.has(defaultCategoryId) ? [defaultCategoryId] : []),
    [defaultCategoryId, validCategoryIds],
  );

  const applyCategoryToItems = useCallback((nextCategories: typeof categories) => {
    setItems((current) =>
      current.map((item) => ({
        ...item,
        categories: item.categories
          .map((category) => nextCategories.find((entry) => entry.id === category.id))
          .filter((category): category is typeof categories[number] => Boolean(category))
          .sort((a, b) => a.name.localeCompare(b.name)),
      })),
    );
  }, [setItems]);

  const openForCreate = useCallback((draft?: EditableDraft) => {
    setEditingItem(null);
    setEditorDraft(draft ?? emptyLibraryDraft());
    setEditorCategoryIds(getInitialEditorCategoryIds());
    setEditorWorkspaceIds(active ? [active.id] : []);
    setMoveTargetWorkspaceId(active?.id ?? "");
    setEditorOpen(true);
  }, [active, getInitialEditorCategoryIds]);

  const openForEdit = useCallback((item: LibraryRecord) => {
    setEditingItem(item);
    setEditorDraft(toDraft(item));
    setEditorCategoryIds(item.categories.map((category) => category.id));
    setEditorWorkspaceIds(item.workspaceIds.length ? item.workspaceIds : [item.workspace_id]);
    setMoveTargetWorkspaceId(item.workspace_id);
    setEditorOpen(true);
    // The in-memory record may carry only the 280-char preview for description/notes.
    // Fetch the full decrypted text so saving without touching the field can't truncate it.
    void fetchLibraryItemDecryptedText(item.workspace_id, item.id).then(({ notes, description }) => {
      setEditorDraft((current) => ({
        ...current,
        description: description ?? current.description ?? null,
        notes: notes ?? current.notes ?? null,
      }));
    });
  }, []);

  const openItemDetail = useCallback((itemId: string) => {
    const next = new URLSearchParams(searchParams);
    next.set("item", itemId);
    setSearchParams(next, { replace: true });
  }, [searchParams, setSearchParams]);

  const closeItemDetail = useCallback(() => {
    if (!searchParams.has("item")) return;
    const next = new URLSearchParams(searchParams);
    next.delete("item");
    setSearchParams(next, { replace: true });
  }, [searchParams, setSearchParams]);

  useEffect(() => {
    setDefaultCategoryId(active?.default_library_category_id ?? null);
  }, [active?.default_library_category_id]);

  // URL → state sync for filters/search/sort
  useMyLibraryUrlSync({
    searchParams,
    setLibrarySearchQuery,
    setSortBy,
    setSelectedCategoryIds,
    setSelectedStatusFilters,
    setSelectedSourceTypes,
  });

  // Workspace switch: reset editor + filter state
  useEffect(() => {
    setSelectedCategoryIds([]);
    setEditorCategoryIds([]);
    setEditorDraft(emptyLibraryDraft());
    setEditingItem(null);
    setEditorOpen(false);
    setLibrarySearchQuery("");
    setSelectedStatusFilters([]);
    setSelectedSourceTypes([]);
    setSortBy("recent");
    setSelectMode(false);
    setSelectedItemIds(new Set());
  }, [active?.id]);

  useEffect(() => {
    setSelectedCategoryIds((current) => current.filter((id) => validCategoryIds.has(id)));
    setEditorCategoryIds((current) => {
      const next = current.filter((id) => validCategoryIds.has(id));
      if (!editingItem && next.length === 0) return getInitialEditorCategoryIds();
      return next;
    });
    if (defaultCategoryId && !validCategoryIds.has(defaultCategoryId)) {
      setDefaultCategoryId(null);
    }
  }, [defaultCategoryId, editingItem, getInitialEditorCategoryIds, validCategoryIds]);

  useEffect(() => {
    if (!linkedItemId || loading || selectedItem) return;
    const next = new URLSearchParams(searchParams);
    next.delete("item");
    setSearchParams(next, { replace: true });
  }, [linkedItemId, loading, searchParams, selectedItem, setSearchParams]);

  useEffect(() => {
    setSelectedItemIds((current) => {
      const validIds = new Set(items.map((item) => item.id));
      const next = new Set(Array.from(current).filter((id) => validIds.has(id)));
      if (next.size === current.size) return current;
      return next;
    });
  }, [items]);

  const languageSummary = useMemo(() => {
    const codes = Array.from(
      new Set(
        items
          .map((item) => item.language_code?.trim().toLowerCase())
          .filter((c): c is string => !!c)
      )
    );
    return codes.slice(0, 4);
  }, [items]);

  const sourceTypeCounts = useMemo(() => {
    const counts: Record<string, number> = {};
    for (const it of items) {
      const key = it.source_type ?? "other";
      counts[key] = (counts[key] ?? 0) + 1;
    }
    return counts;
  }, [items]);

  const filteredItems = useMemo(() => {
    return sortItems(
      items.filter((item) => {
        const matchesCategories = selectedCategoryIds.length === 0
          || selectedCategoryIds.every((categoryId) => item.categories.some((category) => category.id === categoryId));
        const matchesStatus = selectedStatusFilters.length === 0 || selectedStatusFilters.includes(item.status);
        const matchesSourceType = selectedSourceTypes.length === 0
          || (item.source_type ? selectedSourceTypes.includes(item.source_type) : false);
        const matchesWorkspaces = selectedWorkspaceIds.length === 0
          || selectedWorkspaceIds.some((wsId) => item.workspaceIds.includes(wsId) || item.workspace_id === wsId);
        return matchesCategories && matchesStatus && matchesSourceType && matchesWorkspaces && matchesLibrarySearch(item, librarySearchQuery);
      }),
      sortBy,
    );
  }, [items, librarySearchQuery, selectedCategoryIds, selectedStatusFilters, selectedSourceTypes, selectedWorkspaceIds, sortBy]);

  const filteredItemIds = useMemo(() => filteredItems.map((item) => item.id), [filteredItems]);
  const allVisibleSelected = useMemo(
    () => filteredItemIds.length > 0 && filteredItemIds.every((id) => selectedItemIds.has(id)),
    [filteredItemIds, selectedItemIds],
  );
  const {
    toggleItemSelection,
    exitSelectionMode,
    toggleSelectAllVisible,
    handleBulkAssignCategories,
    handleBulkRemoveCategories,
    handleBulkDelete,
    handleBulkSetStatus,
  } = useMyLibraryBulkActions({
    categories,
    filteredItemIds,
    allVisibleSelected,
    selectedItemIds,
    setItems,
    setSelectedItemIds,
    setSelectMode,
    setBulkBusy,
  });

  useEffect(() => {
    if (selectedItem || !selectMode) return;
    if (filteredItems.length === 0 && selectedItemIds.size === 0) {
      setSelectMode(false);
    }
  }, [filteredItems.length, selectMode, selectedItem, selectedItemIds.size]);

  const persist = useMyLibraryPersist({
    active,
    libraryMode,
    items,
    setItems,
    categories,
    editingItem,
    setEditingItem,
    setEditorOpen,
    setImportOpen,
    refreshLibrary,
  });

  const {
    handleCreateCategory,
    handleSetDefaultCategory,
    handleUpdateCategory,
    handleDeleteCategory,
    handleMergeCategory,
  } = useMyLibraryCategoryActions({
    active,
    editingItem,
    defaultCategoryId,
    setDefaultCategoryId,
    setCategories,
    setCategoryUsageCounts,
    setSelectedCategoryIds,
    setEditorCategoryIds,
    applyCategoryToItems,
    refreshLibrary,
  });

  const handleEditorOpenChange = (open: boolean) => {
    setEditorOpen(open);
    if (!open) setEditingItem(null);
  };

  const saveCurrentDraft = () => {
    persist.beginSave({
      draft: editorDraft,
      currentId: editingItem?.id,
      categoryIds: editorCategoryIds,
      workspaceIds: libraryMode === "shared" ? editorWorkspaceIds : undefined,
      closeEditor: true,
      successMessage: editingItem ? "Library item updated" : `${getLibrarySourceTypeLabel(editorDraft.sourceType)} saved to My Library`,
    });
  };

  const handleCreateExcerpt = (item: LibraryRecord) => {
    navigate(`/new-excerpt?libraryItem=${item.id}&returnTo=${encodeURIComponent(myLibraryReturnTo || `my-library:${item.id}`)}`);
  };

  const handleViewExcerpts = (item: LibraryRecord) => {
    navigate(`/excerpts?libraryItem=${item.id}&returnTo=${encodeURIComponent(myLibraryReturnTo || `my-library:${item.id}`)}`);
  };

  const handleReturnToOrigin = () => {
    const href = resolveReturnToHref(returnTo);
    if (href) {
      navigate(href);
      return;
    }
    closeItemDetail();
  };

  const filteredItemNoun = useMemo(() => deriveFilteredItemNoun(selectedSourceTypes), [selectedSourceTypes]);

  const renderEditorForm = (suffix: "mobile" | "desktop") => (
    <MyLibraryEditorBody
      suffix={suffix}
      draft={editorDraft}
      onChange={setEditorDraft}
      editingItem={editingItem}
      active={active}
      workspaces={workspaces}
      libraryMode={libraryMode}
      saving={persist.saving}
      editorWorkspaceIds={editorWorkspaceIds}
      setEditorWorkspaceIds={setEditorWorkspaceIds}
      moveTargetWorkspaceId={moveTargetWorkspaceId}
      setMoveTargetWorkspaceId={setMoveTargetWorkspaceId}
      onMove={() => void persist.moveCurrentItem(moveTargetWorkspaceId)}
      categories={categories}
      editorCategoryIds={editorCategoryIds}
      setEditorCategoryIds={setEditorCategoryIds}
      defaultCategory={defaultCategory}
      isNeutralDefault={isNeutralDefault}
      onCreateCategory={handleCreateCategory}
      onDelete={() => void persist.deleteCurrentItem()}
      onSave={saveCurrentDraft}
    />
  );

  return (
    <>
    <AppSeo title="My Library" />
    <div className="space-y-6 pb-12 md:pb-16">
      <div aria-live="polite" aria-atomic="true" data-testid="invalid-row-live-region" className="sr-only">{importAnnouncement}</div>
      <header className="space-y-4">
        <MyLibraryHeader
          defaultCategory={defaultCategory}
          isNeutralDefault={isNeutralDefault}
          sourceTypeCounts={sourceTypeCounts}
          languageSummary={languageSummary}
          categoriesCount={categories.length}
          onImport={() => setImportOpen(true)}
          onManageCategories={() => setManagerOpen(true)}
          onCreate={() => openForCreate()}
        />

        <MyLibrarySearchPanel
          searchType={search.searchType}
          setSearchType={search.setSearchType}
          searchValue={search.searchValue}
          setSearchValue={search.setSearchValue}
          detectedIdentifier={search.detectedIdentifier}
          onDismissDetection={search.dismissDetection}
          searching={search.searching}
          results={search.results}
          defaultCategory={defaultCategory}
          saving={persist.saving}
          onSaveResult={(result) => persist.saveSearchResultDraft(result, getInitialEditorCategoryIds())}
          onRefineResult={(result) => openForCreate({ ...result, notes: null })}
        />
      </header>

      <section>
        <MyLibraryFiltersBar
          librarySearchQuery={librarySearchQuery}
          setLibrarySearchQuery={setLibrarySearchQuery}
          sortBy={sortBy}
          setSortBy={setSortBy}
          libraryMode={libraryMode}
          workspaces={workspaces}
          selectedWorkspaceIds={selectedWorkspaceIds}
          toggleWorkspace={(id) => setSelectedWorkspaceIds((cur) => cur.includes(id) ? cur.filter((x) => x !== id) : [...cur, id])}
          clearWorkspaces={() => setSelectedWorkspaceIds([])}
          selectedSourceTypes={selectedSourceTypes}
          setSelectedSourceTypes={setSelectedSourceTypes}
          toggleSourceTypeFilter={(t) => setSelectedSourceTypes((cur) => cur.includes(t) ? cur.filter((v) => v !== t) : [...cur, t])}
          categories={categories}
          selectedCategoryIds={selectedCategoryIds}
          setSelectedCategoryIds={setSelectedCategoryIds}
          toggleCategoryFilter={(id) => setSelectedCategoryIds((cur) => cur.includes(id) ? cur.filter((x) => x !== id) : [...cur, id])}
          selectedStatusFilters={selectedStatusFilters}
          setSelectedStatusFilters={setSelectedStatusFilters}
          toggleStatusFilter={(s) => setSelectedStatusFilters((cur) => cur.includes(s) ? cur.filter((v) => v !== s) : [...cur, s])}
          showSelectionToggle={!selectedItem && filteredItems.length > 0}
          selectMode={selectMode}
          onToggleSelectMode={() => (selectMode ? exitSelectionMode() : setSelectMode(true))}
          filteredCount={filteredItems.length}
          filteredItemNoun={filteredItemNoun}
          hasActiveFilters={Boolean(selectedCategoryIds.length || selectedStatusFilters.length || selectedSourceTypes.length || librarySearchQuery.trim())}
        />

        <MyLibraryListSection
          selectedItem={selectedItem}
          loading={loading}
          filteredItems={filteredItems}
          filteredItemNoun={filteredItemNoun}
          itemsCount={items.length}
          linkedExcerpts={linkedExcerpts}
          returnTo={returnTo}
          myLibraryReturnTo={myLibraryReturnTo}
          onReturnToOrigin={handleReturnToOrigin}
          onCloseItemDetail={closeItemDetail}
          onOpenForEdit={openForEdit}
          onCreateExcerpt={handleCreateExcerpt}
          onViewExcerpts={handleViewExcerpts}
          onDeleteSelectedItem={(item) => void persist.deleteItem(item, closeItemDetail)}
          onChangeStatus={persist.handleChangeStatus}
          onOpenItemDetail={openItemDetail}
          selectMode={selectMode}
          selectedItemIds={selectedItemIds}
          allVisibleSelected={allVisibleSelected}
          bulkBusy={bulkBusy}
          categories={categories}
          onToggleSelectAll={toggleSelectAllVisible}
          onExitSelectionMode={exitSelectionMode}
          onToggleItemSelection={toggleItemSelection}
          onAssignCategories={handleBulkAssignCategories}
          onRemoveCategories={handleBulkRemoveCategories}
          onSetStatus={handleBulkSetStatus}
          onBulkDelete={handleBulkDelete}
          onCreateCategory={handleCreateCategory}
        />
      </section>

      <LibraryCategoryManager
        open={managerOpen}
        onOpenChange={setManagerOpen}
        categories={categories}
        defaultCategoryId={defaultCategoryId}
        usageCounts={categoryUsageCounts}
        onUpdate={handleUpdateCategory}
        onDelete={handleDeleteCategory}
        onMerge={handleMergeCategory}
        onSetDefault={handleSetDefaultCategory}
        onCreate={handleCreateCategory}
      />

      <MyLibraryDialogs
        isMobile={isMobile}
        importOpen={importOpen}
        handleImportOpenChange={handleImportOpenChange}
        handleImportEscapeKeyDown={handleImportEscapeKeyDown}
        items={items}
        categories={categories}
        initialCategoryIds={getInitialEditorCategoryIds()}
        saving={persist.saving}
        importAnnouncementSetter={setImportAnnouncement}
        handleImportFocusRestored={handleImportFocusRestored}
        handleImportReviewRowFocusChange={handleImportReviewRowFocusChange}
        myLibraryReturnTo={myLibraryReturnTo}
        onCreateCategory={handleCreateCategory}
        onImportSave={persist.handleImportSave}
        onOpenExisting={openItemDetail}
        editorOpen={editorOpen}
        handleEditorOpenChange={handleEditorOpenChange}
        renderEditorForm={renderEditorForm}
        duplicateDialogOpen={persist.duplicateDialogOpen}
        setDuplicateDialogOpen={persist.setDuplicateDialogOpen}
        duplicateWarnings={persist.duplicateWarnings}
        pendingSave={persist.pendingSave}
        onConfirmSaveAnyway={(p) => void persist.persistLibraryItem(p)}
      />
    </div>
    </>
  );
};

export default MyLibrary;
